/**
 * Request Deduplicator
 * Prevents duplicate API requests by caching ongoing requests and returning the same promise
 * for identical requests made concurrently
 */
class RequestDeduplicator {
  constructor() {
    this.pendingRequests = new Map();
    this.requestCache = new Map();
    this.cacheTimeout = 5000; // 5 seconds cache for GET requests
  }

  /**
   * Generate a unique key for the request
   */
  generateKey(url, options = {}) {
    const method = options.method || 'GET';
    const body = options.body ? JSON.stringify(options.body) : '';
    const headers = options.headers ? JSON.stringify(options.headers) : '';
    return `${method}:${url}:${body}:${headers}`;
  }

  /**
   * Make a deduplicated request
   */
  async makeRequest(url, options = {}, fetchFunction) {
    const key = this.generateKey(url, options);
    const method = options.method || 'GET';

    // For GET requests, check cache first
    if (method === 'GET' && this.requestCache.has(key)) {
      const cached = this.requestCache.get(key);
      if (Date.now() - cached.timestamp < this.cacheTimeout) {
        console.log('🔄 RequestDeduplicator: Using cached response for', method, url);
        return cached.data;
      } else {
        // Remove expired cache entry
        this.requestCache.delete(key);
      }
    }

    // If request is already pending, return the existing promise
    if (this.pendingRequests.has(key)) {
      console.log('🔄 RequestDeduplicator: Request already pending, waiting for', method, url);
      return await this.pendingRequests.get(key);
    }

    // Create new request promise
    console.log('🔄 RequestDeduplicator: Starting new request for', method, url);
    const requestPromise = this._executeRequest(url, options, fetchFunction);
    this.pendingRequests.set(key, requestPromise);

    try {
      const result = await requestPromise;
      
      // Cache GET requests
      if (method === 'GET') {
        this.requestCache.set(key, {
          data: result,
          timestamp: Date.now()
        });
      }
      
      return result;
    } finally {
      // Clean up completed request
      this.pendingRequests.delete(key);
    }
  }

  /**
   * Execute the actual request
   */
  async _executeRequest(url, options, fetchFunction) {
    try {
      return await fetchFunction(url, options);
    } catch (error) {
      // Don't cache errors
      throw error;
    }
  }

  /**
   * Clear all pending requests and cache
   */
  clear() {
    console.log('🔄 RequestDeduplicator: Clearing all pending requests and cache');
    this.pendingRequests.clear();
    this.requestCache.clear();
  }

  /**
   * Clear cache for specific URL pattern
   */
  clearCacheForUrl(urlPattern) {
    const keysToDelete = [];
    for (const [key] of this.requestCache) {
      if (key.includes(urlPattern)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => this.requestCache.delete(key));
    console.log(`🔄 RequestDeduplicator: Cleared cache for ${keysToDelete.length} entries matching "${urlPattern}"`);
  }

  /**
   * Get stats about current state
   */
  getStats() {
    return {
      pendingRequests: this.pendingRequests.size,
      cachedResponses: this.requestCache.size,
      cacheKeys: Array.from(this.requestCache.keys())
    };
  }
}

// Export singleton instance
export const requestDeduplicator = new RequestDeduplicator();

// Export class for testing
export { RequestDeduplicator };
